import { LoginPage, ForgotPasswordPage, ResetPasswordPage, PinPage, NewPasswordPage } from "./pages/auth";
import { CssBaseline, Typography, Button } from "@mui/material";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import HomePage from "./pages/home.page";
import HistoryPage from "./pages/history.page";
import PendantPage from "./pages/pendant.page";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Layout from "components/Layout/Layout";
import { PrivateRoute } from "components/PrivateRoute";
import { setIsMobile } from "redux/features/configSlice";
import { useDispatch } from "react-redux";
import { useState, useEffect, useRef } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { checkStandalone } from "utils/platform";
import { setIsStandalone } from
  "redux/features/configSlice";
import { Modal, Box } from '@mui/material';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import useMobileCheck from "hooks/useMobileCheck";
import DeviceNotSupported from "components/DeviceNotSupported/DeviceNotSupported";
import { isIOS } from 'react-device-detect';
import ShareIos from './assets/share-ios.svg';
import AddIos from './assets/add-ios.svg';
import useWebInstallPrompt from 'hooks/useWebInstallPrompt';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { UserRoles } from "Enums/UserRoles";

function App() {
  const isMobile = useMobileCheck();
  const hasShownToast = useRef(false);
  const [webInstallPrompt, handleWebInstallDeclined, handleWebInstallAccepted] = useWebInstallPrompt();
  const isInStandaloneModeIos = 'standalone' in window.navigator && window.navigator.standalone
  const dispatch = useDispatch();
  const [isLandscapeIos, setIsLandscapeIos] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [showReload, setShowReload] = useState(false);

  const onSWUpdate = (registration: any) => {
    setShowReload(true);
    setWaitingWorker(registration.waiting);
  };

  useEffect(() => {
    dispatch(setIsStandalone(checkStandalone()));
  }, [dispatch]);


  useEffect(() => {
    if ('serviceWorker' in navigator) {
      serviceWorkerRegistration.register({ onUpdate: onSWUpdate });
    }

    // Checks if should display install popup notification:
    if (isIOS && !isInStandaloneModeIos && !hasShownToast.current) {
      toast(<Box>Para instalar la app debes pulsar <img src={ShareIos} height="16" width="16" alt="Share ios" className="ShareIos" /> y después en el menú desplegable, pulsar Añadir a pantalla de inicio  <img src={AddIos} alt="Add ios" height="16" width="16" /></Box>, {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: false
      });
      hasShownToast.current = true;
    }
  }, []);

  useEffect(() => {

    const handleOrientationChange = () => {
      if (isIOS && (window.screen?.orientation?.angle === 90 || window.screen?.orientation?.angle === -90)) {
        setIsLandscapeIos(true);
      } else {
        setIsLandscapeIos(false);
      }
    };

    if (isIOS) {
      window.addEventListener('orientationchange', handleOrientationChange);
    }
  
    return () => {
      if (isIOS) {
        // Elimina el evento solo en dispositivos iOS
        window.removeEventListener('orientationchange', handleOrientationChange);
      }
    };

  }, [isIOS]);

  const reloadPage = () => {
    if (waitingWorker) {
      (waitingWorker as ServiceWorker)?.postMessage({ type: 'SKIP_WAITING' });
    }
    setShowReload(false);
    window.location.reload();
  };

  useEffect(() => {
    const handleResize = () => {
      const isMobileDevice = window.matchMedia("(max-width: 768px)").matches;
      dispatch(setIsMobile(isMobileDevice));
    };

    handleResize(); // Detecta el tamaño de la pantalla al cargar la página
    window.addEventListener("resize", handleResize); // Detecta el cambio de tamaño de la pantalla

    return () => window.removeEventListener("resize", handleResize); // Limpia el event listener al desmontar el componente
  }, [dispatch]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <ToastContainer />
        {showReload &&
          <Box sx={{
            position: "fixed", margin: "0 20px", top: "40px", backgroundColor: "#FFF", borderRadius: "15px",
            boxShadow: "0px 3px 15px rgba(0, 0, 0, 0.1)", display: 'flex', padding: "20px"
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flexDirection: "column", paddingRight: "9px" }}>
              <Box sx={{ color: "#090A0A", fontWeight: '600', fontSize: '.9rem' }}>Nueva versión</Box>
              <Box sx={{ color: "#6C7072", fontSize: '.9rem' }}>Hay disponible una nueva versión</Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
              <Button color="primary" type="button" sx={{
                padding: "4px 12px !important;", fontWeight: "400", backgroundColor: "#49AFB8 !important;", color: "#FFF !important;",
                textTransform: "initial;"
              }} onClick={() => reloadPage()}>Actualizar</Button>
            </Box>
          </Box>
        }

        <Modal
          open={Boolean(webInstallPrompt && !isIOS)}
          onClose={handleWebInstallDeclined}
          aria-labelledby="install-prompt-title"
          aria-describedby="install-prompt-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            bgcolor: 'background.paper',
            border: 'none',
            boxShadow: 24,
            p: 4,
            margin: 'auto',
            padding: '25px !important',
            borderRadius: '15px !important', // Aplica un borde redondeado de 20px
          }}>
            <Typography id="install-prompt-description" variant="body1" sx={{ fontSize: '.9rem' }}>
              Para una mejor experiencia, instala la aplicación en tu dispositivo.
            </Typography>
            <Box className="modal-footer-install" display="flex" justifyContent="space-around" mt={2} gap={1}>
              <Button
                sx={{
                  width: '50% !important', // Ancho del botón al 50% del contenedor
                  padding: '8px 17px !important',
                  textTransform: 'initial',
                  color: "#49AFB8",
                }}
                variant="outlined"
                color="primary"
                className="selectAll"
                onClick={handleWebInstallDeclined}
              >
                No instalar
              </Button>
              <Button
                sx={{
                  width: '50% !important',
                  padding: '8px 17px !important',
                  textTransform: 'capitalize',
                }}
                variant="contained"
                color="primary"
                className="btn-install-yes"
                onClick={handleWebInstallAccepted}
              >
                Instalar
              </Button>

            </Box>
          </Box>
        </Modal>
        <Modal
          open={isLandscapeIos}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          sx={{
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.9)', // Color de fondo más oscuro y más opaco
            }
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isLandscapeIos ? '60%' : '80%', // Más ancho en landscape
            bgcolor: 'background.paper',
            border: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            outline: 0,
            boxShadow: 24,
            p: 4,
          }}>

            <Box>
              <ScreenRotationIcon sx={{ fontSize: '80px', pr: '16px' }} />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <h2 id="modal-title">Gira tu dispositivo</h2>
              <p id="modal-description">
                {isLandscapeIos ? "Gira tu dispositivo al modo retrato." : "Gracias por usar el modo retrato."}
              </p>
            </Box>
          </Box>
        </Modal>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/reset-password/:resetToken"
              element={<ResetPasswordPage />}
            />
            <Route path="/pin" element={<PinPage />} />
            <Route
              path="/verify-email/:resetToken"
              element={<NewPasswordPage />}
            />
            <Route
              path="/"
              element={
                <PrivateRoute allowedRoles={[UserRoles.TECHNICIAN]}>
                  <Layout>
                    <HomePage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/history"
              element={
                <PrivateRoute allowedRoles={[UserRoles.TECHNICIAN]}>
                  <Layout>
                    <HistoryPage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/pendant"
              element={
                <PrivateRoute allowedRoles={[UserRoles.TECHNICIAN]}>
                  <Layout>
                    <PendantPage />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route path="/*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </LocalizationProvider>
    </>
  );
}

export default App;
